/**
 * Recovery from "stale chunks" after a deploy.
 *
 * A tab opened before a deploy still runs the old `index-<hash>.js`, which
 * lazy-imports views by their old hashed names. Netlify deploys are atomic, so
 * those files are gone: the request falls through to the SPA redirect (or a 404)
 * and the dynamic import fails. The only fix is to load the new index.html, so
 * the failed navigation is turned into a full page load of the same URL.
 */

const RELOAD_KEY = 'travick:stale-chunk-reload'
/** A second failure inside this window means a reload did not help: stop. */
const RELOAD_GUARD_MS = 10_000

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's CSS preload helper
  'Expected a JavaScript-or-Wasm module script', // HTML served in place of a chunk
]

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * Hard-load `url` (default: the current page) unless we already did so moments
 * ago — a missing chunk that survives a fresh index.html is a real bug, and
 * reloading forever would hide it behind a blinking page.
 *
 * @returns whether a reload was started
 */
export function reloadForStaleChunk(url?: string): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (Date.now() - last < RELOAD_GUARD_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage blocked: still worth one reload, the guard is a safety net only.
  }

  if (url) window.location.assign(url)
  else window.location.reload()
  return true
}
